export const DATA_TABLE_ACCESS_MODE_OPTIONS: {
  value: DataTableAccessMode;
  label: string;
  description: string;
}[] = [
  {
    value: "FULL_TABLE",
    label: "Full table access",
    description: "Reads and writes every row in the table.",
  },
  {
    value: "ASSIGNED_ROWS",
    label: "Assigned rows only",
    description:
      "Reads and updates only the row of the task it's handling.",
  },
  {
    value: "WRITE_ONLY",
    label: "Append only",
    description: "Adds new rows and can't read or change existing ones.",
  },
];

export const DATA_TABLE_ACCESS_MODE_ACTIONS: Record<DataTableAccessMode, string[]> = {
  ASSIGNED_ROWS: ["Update row", "Get row"],
  WRITE_ONLY: ["Add row"],
  FULL_TABLE: ["List rows", "Add row", "Update row", "Delete row", "Get row"],
};

/** Default when connecting a table — Full table for all teams. */
export const DEFAULT_DATA_TABLE_ACCESS_MODE: DataTableAccessMode = "FULL_TABLE";

/** Tools allowed per access mode — mirrors backend derivation of allowed_tools. */
export const DATA_TABLE_ACCESS_MODE_ALLOWED_TOOLS: Record<
  DataTableAccessMode,
  DataTableRowTool[]
> = {
  ASSIGNED_ROWS: ["GET_ROW", "UPDATE_ROW"],
  WRITE_ONLY: ["ADD_ROW"],
  FULL_TABLE: [
    "LIST_ROWS",
    "GET_ROW",
    "ADD_ROW",
    "UPDATE_ROW",
    "DELETE_ROW",
  ],
};

export const DATA_TABLE_ROW_TOOL_OPTIONS: {
  value: DataTableRowTool;
  label: string;
  description: string;
}[] = [
  {
    value: "LIST_ROWS",
    label: "List Rows",
    description: "List rows from this data table",
  },
  {
    value: "GET_ROW",
    label: "Get Row",
    description: "Fetch a single row by ID from this data table",
  },
  {
    value: "ADD_ROW",
    label: "Add Row",
    description: "Create a new row in this data table",
  },
  {
    value: "UPDATE_ROW",
    label: "Update Row",
    description: "Update an existing row in this data table",
  },
  {
    value: "DELETE_ROW",
    label: "Delete Row",
    description: "Delete a row from this data table",
  },
];

export function getDataTableAccessModeOption(
  mode: DataTableAccessMode | null | undefined
) {
  const resolved = resolveDataTableAccessMode(mode);
  return (
    DATA_TABLE_ACCESS_MODE_OPTIONS.find((item) => item.value === resolved) ??
    DATA_TABLE_ACCESS_MODE_OPTIONS[0]
  );
}

export function formatDataTableAccessMode(
  mode: DataTableAccessMode | null | undefined
): string {
  return getDataTableAccessModeOption(mode).label;
}

export const DATA_TABLE_ROW_TOOL_COMPACT_LABELS: Record<
  DataTableRowTool,
  string
> = {
  LIST_ROWS: "List rows",
  GET_ROW: "Get row",
  ADD_ROW: "Add row",
  UPDATE_ROW: "Update row",
  DELETE_ROW: "Delete row",
};

export function formatDataTableRowToolCompactLabel(
  tool: DataTableRowTool
): string {
  return DATA_TABLE_ROW_TOOL_COMPACT_LABELS[tool] ?? tool;
}

export function isDataTableAccessMode(
  value: unknown
): value is DataTableAccessMode {
  return (
    value === "FULL_TABLE" ||
    value === "WRITE_ONLY" ||
    value === "ASSIGNED_ROWS"
  );
}

export function resolveDataTableAccessMode(
  value: unknown
): DataTableAccessMode {
  return isDataTableAccessMode(value)
    ? value
    : DEFAULT_DATA_TABLE_ACCESS_MODE;
}

export function getDataTableAllowedTools(
  accessMode: DataTableAccessMode | null | undefined
): DataTableRowTool[] {
  return DATA_TABLE_ACCESS_MODE_ALLOWED_TOOLS[
    resolveDataTableAccessMode(accessMode)
  ];
}

/** Payload shape for connect/PATCH — never includes derived allowed_tools. */
export function toDataTableIntegrationPayload(
  integration: DataTableIntegration
): Pick<
  DataTableIntegration,
  "data_table_id" | "access_mode" | "usage_instructions"
> {
  return {
    data_table_id: integration.data_table_id,
    access_mode: resolveDataTableAccessMode(integration.access_mode),
    usage_instructions: integration.usage_instructions ?? "",
  };
}

export function sanitizeDataTableIntegrations(
  integrations: DataTableIntegration[] | null | undefined
): DataTableIntegration[] {
  return (integrations ?? []).map(toDataTableIntegrationPayload);
}

export function normalizeDataTableIntegrationsForCompare(
  integrations: DataTableIntegration[]
): string {
  return JSON.stringify(
    [...integrations]
      .map((integration) => toDataTableIntegrationPayload(integration))
      .sort((a, b) => a.data_table_id.localeCompare(b.data_table_id))
  );
}

export function mergeDataTableIntegrationsSelection(
  selectedIds: string[],
  currentIntegrations: DataTableIntegration[],
  savedIntegrations?: DataTableIntegration[]
): DataTableIntegration[] {
  const currentIds = new Set(
    currentIntegrations.map((integration) => integration.data_table_id)
  );
  const newlyAddedIds = selectedIds.filter((id) => !currentIds.has(id));
  const existingIds = selectedIds.filter((id) => currentIds.has(id));
  const orderedIds = [...newlyAddedIds, ...existingIds];

  return orderedIds.map((dataTableId) => {
    const current = currentIntegrations.find(
      (integration) => integration.data_table_id === dataTableId
    );
    if (current) {
      return toDataTableIntegrationPayload(current);
    }

    const saved = savedIntegrations?.find(
      (integration) => integration.data_table_id === dataTableId
    );
    if (saved) {
      return toDataTableIntegrationPayload(saved);
    }

    return {
      data_table_id: dataTableId,
      access_mode: DEFAULT_DATA_TABLE_ACCESS_MODE,
      usage_instructions: "",
    };
  });
}
